import { apiRequest, USE_MOCKS } from './client'
import type {
  NodeSummary,
  NodeListResponse,
  NodeDetail,
  NodeCamerasResponse,
  NodeConfigStateResponse,
  CommandListResponse,
  CreateCommandRequest,
  CreateCommandResponse,
  FileListResponse,
  FileLocalState,
  InferenceAutoUploadState,
  RecordingAutoUploadState,
  RunRecordingAutoUploadResponse,
  RunInferenceAutoUploadResponse,
  UpdateRecordingAutoUploadRequest,
  UpdateInferenceAutoUploadRequest,
  UpdateNodeRequest,
} from '@/types'
import { mockNodes, mockNodeDetail, updateMockNode } from '@/mocks/nodes'
import { mockCommands } from '@/mocks/commands'
import { mockFiles } from '@/mocks/files'

export interface NodeDeletionPreview {
  node_id: string
  guardian_node_count: number
}

export function previewNodeDeletion(nodeId: string): Promise<NodeDeletionPreview> {
  return apiRequest<NodeDeletionPreview>(`/api/nodes/${nodeId}/deletion-preview`)
}

export function deleteNode(nodeId: string): Promise<void> {
  return apiRequest<void>(`/api/nodes/${nodeId}`, { method: 'DELETE' })
}

export async function listNodes(params?: {
  company?: string
  site?: string
  status?: string
  limit?: number
  offset?: number
}): Promise<NodeListResponse> {
  if (USE_MOCKS) return mockNodes(params)
  const qs = new URLSearchParams()
  if (params?.company) qs.set('company', params.company)
  if (params?.site) qs.set('site', params.site)
  if (params?.status) qs.set('status', params.status)
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.offset != null) qs.set('offset', String(params.offset))
  const query = qs.toString() ? `?${qs}` : ''
  return apiRequest<NodeListResponse>(`/api/nodes${query}`)
}

export async function getNode(nodeId: string): Promise<NodeDetail> {
  if (USE_MOCKS) return mockNodeDetail(nodeId)
  return apiRequest<NodeDetail>(`/api/nodes/${nodeId}`)
}

export async function updateNode(
  nodeId: string,
  body: UpdateNodeRequest,
): Promise<NodeSummary> {
  if (USE_MOCKS) return updateMockNode(nodeId, body)
  return apiRequest<NodeSummary>(`/api/nodes/${nodeId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export async function updateNodeName(
  nodeId: string,
  body: UpdateNodeRequest,
): Promise<NodeSummary> {
  return updateNode(nodeId, body)
}

export async function listNodeFiles(
  nodeId: string,
  params?: {
    file_kind?: 'recording' | 'inference_recording'
    local_state?: FileLocalState
    camera_keys?: string[]
    limit?: number
    offset?: number
  },
): Promise<FileListResponse> {
  if (USE_MOCKS) {
    return mockFiles(nodeId, params?.file_kind ?? 'recording', {
      localState: params?.local_state,
      cameraKeys: params?.camera_keys,
      limit: params?.limit,
      offset: params?.offset,
    })
  }
  const qs = new URLSearchParams()
  if (params?.file_kind) qs.set('file_kind', params.file_kind)
  if (params?.local_state) qs.set('local_state', params.local_state)
  params?.camera_keys?.forEach((cameraKey) => qs.append('camera_keys', cameraKey))
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.offset != null) qs.set('offset', String(params.offset))
  const query = qs.toString() ? `?${qs}` : ''
  return apiRequest<FileListResponse>(`/api/nodes/${nodeId}/files${query}`)
}

export async function deleteNodeFileLocal(
  nodeId: string,
  fileId: string,
): Promise<CreateCommandResponse> {
  return apiRequest<CreateCommandResponse>(
    `/api/nodes/${encodeURIComponent(nodeId)}/files/${encodeURIComponent(fileId)}/delete-local`,
    { method: 'POST' },
  )
}

export async function listNodeCommands(nodeId: string): Promise<CommandListResponse> {
  if (USE_MOCKS) return mockCommands(nodeId)
  return apiRequest<CommandListResponse>(`/api/nodes/${nodeId}/commands`)
}

export async function createCommand(
  nodeId: string,
  body: CreateCommandRequest,
): Promise<CreateCommandResponse> {
  return apiRequest<CreateCommandResponse>(`/api/nodes/${nodeId}/commands`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function getNodeCameras(nodeId: string): Promise<NodeCamerasResponse> {
  if (USE_MOCKS) {
    return {
      items: [
        { key: 'cam0', name: 'Main Entrance', source_preview: 'rtsp://rtsp-source:8554/...', recording_status: 'idle', recording_pid: null, recording_started_by: null, fps: 5 },
        { key: 'cam1', name: 'Back Door', source_preview: 'rtsp://rtsp-source:8554/...', recording_status: 'idle', recording_pid: null, recording_started_by: null, fps: 5 },
        { key: 'cam2', name: 'Parking Lot', source_preview: 'rtsp://rtsp-source:8554/...', recording_status: 'idle', recording_pid: null, recording_started_by: null, fps: 5 },
      ],
    }
  }
  return apiRequest<NodeCamerasResponse>(`/api/nodes/${nodeId}/cameras`)
}

export async function getNodeConfigState(nodeId: string): Promise<NodeConfigStateResponse> {
  if (USE_MOCKS) {
    // Mock: simulate a node that is slightly behind (desired=5, applied=4)
    return {
      node_id: nodeId,
      desired_revision: 5,
      applied_revision: 4,
      last_fetched_at: new Date(Date.now() - 120000).toISOString(),
      last_applied_at: new Date(Date.now() - 300000).toISOString(),
      error_message: null,
    }
  }
  return apiRequest<NodeConfigStateResponse>(`/api/nodes/${nodeId}/config-state`)
}

export async function updateInferenceAutoUpload(
  nodeId: string,
  body: UpdateInferenceAutoUploadRequest,
): Promise<InferenceAutoUploadState> {
  if (USE_MOCKS) {
    return {
      status: body.enabled ? 'on' : 'off',
      supported: true,
      inference_enabled: true,
      enabled: body.enabled,
      time: body.time,
      target: body.target,
      clean_after_upload: body.clean_after_upload,
    }
  }
  return apiRequest<InferenceAutoUploadState>(`/api/nodes/${nodeId}/inference-recording/auto-upload`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function runInferenceAutoUploadNow(
  nodeId: string,
): Promise<RunInferenceAutoUploadResponse> {
  if (USE_MOCKS) {
    return { node_id: nodeId, commands_created: 1 }
  }
  return apiRequest<RunInferenceAutoUploadResponse>(`/api/nodes/${nodeId}/inference-recording/auto-upload/run`, {
    method: 'POST',
  })
}

export async function updateRecordingAutoUpload(
  nodeId: string,
  body: UpdateRecordingAutoUploadRequest,
): Promise<RecordingAutoUploadState> {
  if (USE_MOCKS) {
    const selected = body.mode === 'inference' ? 'inference' : 'manual'
    const kind = {
      supported: true,
      enabled: body.mode !== 'off',
      local_time: body.local_time,
      timezone: body.timezone,
      file_window: body.file_window,
      delete_local_after_success: body.delete_local_after_success,
    }
    return {
      mode: body.mode,
      manual: body.mode === 'manual' ? kind : { ...kind, enabled: false },
      inference: selected === 'inference' ? kind : { ...kind, enabled: false },
    }
  }
  return apiRequest<RecordingAutoUploadState>(`/api/nodes/${nodeId}/recording-auto-upload`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function runRecordingAutoUploadNow(
  nodeId: string,
): Promise<RunRecordingAutoUploadResponse> {
  if (USE_MOCKS) {
    return { node_id: nodeId, mode: 'manual', commands_created: 1 }
  }
  return apiRequest<RunRecordingAutoUploadResponse>(`/api/nodes/${nodeId}/recording-auto-upload/run`, {
    method: 'POST',
  })
}
